import {
  DEFAULT_PLANNER_REMINDER_PREFERENCES,
  normalisePlannerReminderPreferences
} from './reminder-preferences.js';
import {createPlannerReminderPlan} from './reminder-plan.js';

function requireAdapter(adapter){
  if(!adapter?.reconcile||!adapter?.cancelOwned||!adapter?.ensurePermission){
    throw new TypeError('Planner local notification adapter is required');
  }
  return adapter;
}

export function createPlannerReminderScheduler({
  adapter,
  preferenceStore,
  listSessions,
  now=Date.now
}={}){
  const notifications=requireAdapter(adapter);
  if(!preferenceStore?.get||!preferenceStore?.save){
    throw new TypeError('preferenceStore is required');
  }
  if(typeof listSessions!=='function')throw new TypeError('listSessions is required');

  async function getPreferences(){
    return normalisePlannerReminderPreferences(await preferenceStore.get());
  }

  async function enable(changes={}){
    const permission=await notifications.ensurePermission({request:true});
    if(permission.status!=='granted'){
      await preferenceStore.save({
        ...DEFAULT_PLANNER_REMINDER_PREFERENCES,
        ...changes,
        enabled:false
      });
      await notifications.cancelOwned();
      return Object.freeze({
        status:permission.supported===false?'unsupported':'permission_denied',
        permission:permission.status,
        preferences:await getPreferences()
      });
    }
    const preferences=await preferenceStore.save({...changes,enabled:true});
    const result=await reconcileAll({preferences});
    return Object.freeze({...result,status:'enabled',permission:'granted',preferences});
  }

  async function configure(changes={}){
    const current=await getPreferences();
    if(changes.enabled===true&&current.enabled!==true)return enable(changes);
    const preferences=await preferenceStore.save({...current,...changes});
    if(preferences.enabled!==true){
      const result=await notifications.cancelOwned();
      return Object.freeze({status:'disabled',preferences,...result});
    }
    const result=await reconcileAll({preferences});
    return Object.freeze({...result,status:'configured',preferences});
  }

  async function disable(){
    const preferences=await preferenceStore.save({enabled:false});
    const result=await notifications.cancelOwned();
    return Object.freeze({status:'disabled',preferences,...result});
  }

  async function reconcileAll({preferences=null}={}){
    const prefs=preferences||await getPreferences();
    if(prefs.enabled!==true){
      const result=await notifications.cancelOwned();
      return Object.freeze({status:'disabled',preferences:prefs,...result});
    }

    const sessions=[...(await listSessions()||[])];
    const desired=[];
    for(const session of sessions){
      if(session?.deletedAt)continue;
      if(['completed','cancelled'].includes(String(session?.status||'')))continue;
      desired.push(...createPlannerReminderPlan({session,preferences:prefs,now}));
    }
    const result=await notifications.reconcile(desired);
    return Object.freeze({
      ...result,
      preferences:prefs,
      desiredCount:desired.length,
      sessionCount:sessions.length
    });
  }

  async function onInteraction(listener){
    if(typeof notifications.onAction!=='function'){
      return Object.freeze({remove:async()=>{}});
    }
    return notifications.onAction(listener);
  }

  return Object.freeze({
    getPreferences,enable,configure,disable,reconcileAll,onInteraction
  });
}
